// src/sectors/pilates/sessions/components/SessionDetailDialog/SessionParticipantsCard.jsx
import React from 'react';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import Chip from '@mui/material/Chip';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

import { SoftCard, MiniTag, StatPill, toneColor } from './SessionDetailAtoms';
import { TR, statusMeta } from './sessionDetail.utils';

function pillSx(theme, c) {
  return {
    height: 26,
    borderRadius: '999px',
    fontWeight: 1000,
    bgcolor: alpha(c, theme.palette.mode === 'dark' ? 0.16 : 0.12),
    border: `1px solid ${alpha(c, 0.26)}`,
    '& .MuiChip-label': { px: 1.0 },
    '& .MuiChip-icon': { ml: 0.6, mr: -0.2 },
  };
}

function moneyChipSx(theme) {
  return {
    height: 26,
    borderRadius: '999px',
    fontWeight: 1000,
    bgcolor: theme.palette.mode === 'dark' ? alpha('#fff', 0.04) : alpha('#0b1220', 0.035),
    border: `1px solid ${alpha(
      theme.palette.text.primary,
      theme.palette.mode === 'dark' ? 0.14 : 0.1
    )}`,
    '& .MuiChip-label': { px: 1.0 },
  };
}

function modeChipTone(modeUpper) {
  if (modeUpper === 'PACKAGE') return 'info';
  if (modeUpper === 'PAID') return 'warning';
  return 'secondary';
}
function debtChipSx(theme) {
  const c = theme.palette.error.main;
  return {
    height: 26,
    borderRadius: '999px',
    fontWeight: 1000,
    bgcolor: alpha(c, theme.palette.mode === 'dark' ? 0.18 : 0.12),
    border: `1px solid ${alpha(c, 0.32)}`,
    color: c,
    '& .MuiChip-label': { px: 1.0 },
  };
}

export function SessionParticipantsCard({
  s,
  ps,
  isPaid,
  isPackage,
  booked,
  cap,
  pCounts,
  borderSoft,
}) {
  const theme = useMuiTheme();
  const isDark = theme.palette.mode === 'dark';

  if (!s) return null;

  return (
    <SoftCard tone="blue">
      {/* header */}
      <MDBox
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
      >
        <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <Icon sx={{ opacity: 0.85 }}>groups</Icon>
          <MDTypography variant="button" sx={{ fontWeight: 1000, opacity: 0.92 }}>
            Katılımcılar
          </MDTypography>
        </MDBox>

        <MiniTag tone="mint" icon="groups" label={`${booked} kişi`} />
      </MDBox>

      {/* stats */}
      <MDBox sx={{ display: 'flex', gap: 0.7, flexWrap: 'wrap', mt: 0.6 }}>
        <StatPill label="Geldi" value={pCounts.attended} tone="mint" />
        <StatPill label="Gelmedi" value={pCounts.noShow} tone="red" />
        <StatPill label="Beklemede" value={pCounts.pending} tone="blue" />
        {pCounts.cancelled ? (
          <StatPill label="İptal" value={pCounts.cancelled} tone="blue" />
        ) : null}
      </MDBox>

      <Divider sx={{ my: 1 }} />

      {!ps?.length ? (
        <MDTypography variant="body2" sx={{ opacity: 0.7 }}>
          Bu seansa kayıtlı katılımcı bulunmuyor.
        </MDTypography>
      ) : (
        <MDBox sx={{ display: 'grid', gap: 0.55 }}>
          {ps.map((p, i) => {
            const meta = statusMeta(p.attendanceStatus);
            const c = toneColor(theme, meta.tone);

            const billingModeLabel = TR.billingMode(p.billingMode || s.billingMode);
            const billingModeUpper = String(p.billingMode || s.billingMode || '').toUpperCase();

            const hasPackageLine = isPackage && (p.packagePlanName || p.memberPackageId);
            const packageLine = hasPackageLine
              ? `Paket: ${p.packagePlanName || `#${p.memberPackageId}`}${
                  p.packageStartAt ? ` • Başlangıç: ${p.packageStartAt}` : ''
                }${p.packageEstimatedEndAt ? ` • Bitiş: ${p.packageEstimatedEndAt}` : ''}`
              : null;

            return (
              <MDBox
                key={p.id ?? `${p.fullName}-${i}`}
                sx={{
                  cursor: 'default',
                  p: 0.95,
                  borderRadius: '16px',
                  border: borderSoft,
                  background: isDark ? alpha('#fff', 0.03) : alpha('#fff', 0.9),
                  display: 'grid',
                  gap: 0.55,
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'background .12s ease',
                  '&:hover': {
                    background: isDark ? alpha('#fff', 0.04) : alpha('#0b1220', 0.015),
                  },
                }}
              >
                {/* left accent line (ince ve sakin) */}
                <MDBox
                  sx={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 4,
                    background: alpha(c, 0.85),
                  }}
                />

                {/* top row */}
                <MDBox
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    minWidth: 0,
                  }}
                >
                  {/* name + meta */}
                  <MDBox sx={{ minWidth: 0, display: 'grid', gap: 0.2 }}>
                    <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 0.65, minWidth: 0 }}>
                      <Icon sx={{ fontSize: 18, color: c }}>{meta.icon}</Icon>
                      <MDTypography variant="body2" sx={{ fontWeight: 1000 }} noWrap>
                        {p.fullName}
                      </MDTypography>
                    </MDBox>

                    <MDTypography variant="caption" sx={{ opacity: 0.72, fontWeight: 900 }} noWrap>
                      {billingModeLabel}
                    </MDTypography>
                  </MDBox>

                  {/* right pills */}
                  <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                    {/* status pill (en önemli) */}
                    <Chip
                      size="small"
                      icon={<Icon sx={{ fontSize: 16 }}>{meta.icon}</Icon>}
                      label={meta.label}
                      sx={pillSx(theme, c)}
                    />

                    {/* mode pill (paket/ücretli/normal) */}

                    {/* money (varsa) */}
                    {isPaid ? (
                      <Chip
                        size="small"
                        label={p.priceTry != null ? `Ücret ₺${p.priceTry}` : 'Ücret ₺—'}
                        sx={moneyChipSx(theme)}
                      />
                    ) : null}
                    {Number(p.debtTry) > 0 ? (
                      <Chip size="small" label={`Borç ₺${p.debtTry}`} sx={debtChipSx(theme)} />
                    ) : null}
                  </MDBox>
                </MDBox>

                {/* package line */}
                {packageLine ? (
                  <MDTypography
                    variant="caption"
                    sx={{
                      opacity: 0.9,
                      fontWeight: 900,
                      pl: 0.2,
                      color: isDark ? alpha('#fff', 0.85) : alpha('#0b1220', 0.78),
                    }}
                    noWrap
                    title={packageLine}
                  >
                    {packageLine}
                  </MDTypography>
                ) : null}
              </MDBox>
            );
          })}
        </MDBox>
      )}
    </SoftCard>
  );
}
